/** Optional size hints and unit helpers for CAD prompts. */

export const CAD_UNITS = [
  { id: "mm", label: "Millimeters (mm)", suffix: "mm" },
  { id: "cm", label: "Centimeters (cm)", suffix: "cm" },
  { id: "in", label: "Inches (in)", suffix: "in" },
];

const TO_MM = { mm: 1, cm: 10, in: 25.4 };

export const CAD_PRESETS = [
  "Phone stand with 45° viewing angle, stable base, about 80mm wide",
  "Enclosure box 100x60x30mm with 4 corner mounting holes, 3mm walls",
  "Parametric bracket with two 5mm holes spaced 30mm apart, 3mm thick",
  "Rounded storage tray 120x80x25mm with 3mm wall thickness",
  "Custom gear with 20 teeth, 40mm outer diameter, 5mm thick",
  "L-shaped desk cable clip that holds two USB cables",
];

export function unitSuffix(unit) {
  return CAD_UNITS.find((u) => u.id === unit)?.suffix || "mm";
}

export function toMillimeters(value, unit = "mm") {
  return (Number(value) || 0) * (TO_MM[unit] || 1);
}

export function fromMillimeters(mm, unit = "mm") {
  const factor = TO_MM[unit] || 1;
  return (Number(mm) || 0) / factor;
}

function roundForUnit(value, unit) {
  const n = Number(value) || 0;
  if (unit === "in") return Math.round(n * 1000) / 1000;
  if (unit === "cm") return Math.round(n * 100) / 100;
  return Math.round(n * 10) / 10;
}

export function convertSizeHints(hints, nextUnit) {
  const prevUnit = hints.unit || "mm";
  if (prevUnit === nextUnit) return hints;

  const keys = ["length", "width", "height", "wallThickness", "fillet"];
  const next = { ...hints, unit: nextUnit };
  for (const key of keys) {
    if (!hints[key]) continue;
    const mm = toMillimeters(hints[key], prevUnit);
    next[key] = roundForUnit(fromMillimeters(mm, nextUnit), nextUnit);
  }
  return next;
}

/** Combine a free-form description with optional dimension hints (always sent to AI in mm). */
export function buildCadPrompt(description, sizeHints = {}) {
  const text = String(description || "").trim();
  if (!text) return "";

  const unit = sizeHints.unit || "mm";
  const extras = [];

  const length = toMillimeters(sizeHints.length, unit);
  const width = toMillimeters(sizeHints.width, unit);
  const height = toMillimeters(sizeHints.height, unit);
  if (length > 0 && width > 0 && height > 0) {
    extras.push(`target size about ${length}mm x ${width}mm x ${height}mm`);
  }

  const wall = toMillimeters(sizeHints.wallThickness, unit);
  if (wall > 0) extras.push(`${wall}mm wall or plate thickness`);

  const fillet = toMillimeters(sizeHints.fillet, unit);
  if (fillet > 0) extras.push(`${fillet}mm corner fillet`);

  if (!extras.length) return text;
  return `${text}. ${extras.join(", ")}.`;
}

export const EMPTY_SIZE_HINTS = {
  unit: "mm",
  length: "",
  width: "",
  height: "",
  wallThickness: "",
  fillet: "",
};
